import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { WaitlistEntry } from '../types';

function formatDate(ts: string): string {
  return new Date(ts).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

function exportCSV(entries: WaitlistEntry[]) {
  const rows = [['ID', 'Email', 'Role', 'Signed Up'].join(',')];
  entries.forEach(e => {
    rows.push([
      String(e.id),
      `"${e.email || ''}"`,
      `"${e.role || ''}"`,
      `"${formatDate(e.created_at)}"`,
    ].join(','));
  });
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `bthd-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const ROLE_COLORS: Record<string, string> = {
  athlete:      '#014c8d',
  coach:        '#0fa86d',
  organization: '#bf1e2e',
  mentor:       '#e88c0e',
  other:        '#4a6080',
};

function roleColor(role: string | null): string {
  const key = (role || '').toLowerCase();
  return ROLE_COLORS[key] || ROLE_COLORS.other;
}

export default function WaitlistPage() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    supabase
      .from('waitlist')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setEntries((data as WaitlistEntry[]) ?? []);
        setLoading(false);
      });
  }, []);

  const filtered = entries.filter(e => {
    if (!search) return true;
    return (e.email || '').toLowerCase().includes(search.toLowerCase()) ||
           (e.role || '').toLowerCase().includes(search.toLowerCase());
  });

  // Role breakdown
  const roleCounts: Record<string, number> = {};
  entries.forEach(e => {
    const k = e.role || 'Unknown';
    roleCounts[k] = (roleCounts[k] || 0) + 1;
  });

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 28, color: 'var(--text)' }}>Waitlist</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>
            {loading ? 'Loading...' : `${entries.length} signup${entries.length !== 1 ? 's' : ''}`}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => exportCSV(filtered)} disabled={filtered.length === 0}>
          ↓ Export CSV
        </button>
      </div>

      {/* Role breakdown pills */}
      {!loading && entries.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {Object.entries(roleCounts).sort((a, b) => b[1] - a[1]).map(([role, count]) => {
            const color = roleColor(role);
            return (
              <div key={role} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, background: color + '18', border: `1.5px solid ${color}44` }}>
                <span style={{ fontWeight: 700, fontSize: 12, color, textTransform: 'capitalize' }}>{role}</span>
                <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 15, color }}>{count}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by email or role..."
          style={{
            width: '100%', maxWidth: 360,
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            color: 'var(--text)', borderRadius: 8, padding: '8px 12px',
            fontSize: 13, outline: 'none', fontFamily: 'Barlow, sans-serif',
          }}
        />
      </div>

      {/* Cards grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>Loading waitlist...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>
          {search ? 'No results match your search.' : 'No waitlist signups yet.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {filtered.map(entry => {
            const color = roleColor(entry.role);
            const emailParts = (entry.email || '').split('@');
            const emailHandle = emailParts[0];
            const emailDomain = emailParts[1] ? `@${emailParts[1]}` : '';
            return (
              <div key={entry.id} style={{ background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: color }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: color + '22', border: `1.5px solid ${color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color, fontWeight: 700, flexShrink: 0 }}>
                    {emailHandle[0]?.toUpperCase() || '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span>{emailHandle}</span><span style={{ color: 'var(--text-3)' }}>{emailDomain}</span>
                    </div>
                    <div style={{ fontSize: 10, color, fontWeight: 700, textTransform: 'capitalize', marginTop: 2 }}>{entry.role || 'Unknown role'}</div>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Signed up {formatDate(entry.created_at)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
